import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ONBOARDING_V2 } from '../../components/onboarding/onboardingV2Routes';
import { FondoCordillera } from '../../components/onboarding/marca/FondoCordillera';
import { GuiaCumbre, useDialogo, type Gesto } from '../../components/onboarding/marca/GuiaCumbre';
import { useUserGroup } from '../../hooks/useUserGroup';
import { V2_STUDIO_PATH } from '../../lib/userGroups';
import type { Tramo } from '../../lib/onboardingMarca/dialogo';
import { leerMarca, type MarcaGuardada } from '../../lib/onboardingMarca/marca';
import { leerEstado, limpiarEstado } from '../../lib/onboardingMarca/estado';
import { DESCRIBIR_MARCA, enDescribirMarca, enMarcaNueva, MARCA_NUEVA } from '../../lib/onboardingMarca/marcaNueva';
import { marcarLeidas } from '../../lib/notificaciones';
import { isPaidPlan } from '../../lib/planCapabilities';
import { supabase } from '../../lib/supabase';
import { destinoAlTerminar, marcarOnboardingDeMarcaHecho } from '../../lib/onboardingMarca/finDelOnboarding';
import { getUserPlan } from '../../lib/planUtils';
import RecargaAutomatica from '../../components/facturacionPorMarca/RecargaAutomatica';
import { recargaDeBilletera, type RecargaDeMarca } from '../../components/facturacionPorMarca/recargaDeMarca';

/** Lo que tarda, como mucho, la marca en llegar antes de que Cumbre hable igual. */
const ESPERA_MAX_MARCA_MS = 1500;

/** Los colores del confeti cuando la marca no trae paleta: los del branding activo (Atlas o el del cliente). */
const COLORES_DE_ATLAS = ['var(--brand-primary)', 'var(--brand-accent)', 'var(--brand-black)', '#FFC93C', '#FF6B4A'];
const PAPELITOS = 26;

function pideMenosMovimiento(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function dialogoDelFinal(nombre: string, conPiezas: boolean, nueva = false): Tramo[] {
  return [
    { texto: '¡Listo!', estilo: 'saludo' },
    { texto: `${nombre} ya está en Atlas`, estilo: 'pregunta' },
    {
      texto: nueva
        ? 'Ya es tu marca activa: lo que crees desde ahora usa su logo, sus colores y su forma de comunicar. Cambias de marca desde el selector.'
        : conPiezas
        ? 'Todo lo que crees desde ahora usa tu logo, tus colores y tu forma de comunicar. Tus primeras piezas quedaron en Creaciones.'
        : 'Todo lo que crees desde ahora usa tu logo, tus colores y tu forma de comunicar.',
      estilo: 'detalle',
    },
  ];
}

/**
 * A6 · Listo (ADR-2026-09-28-onboarding-v2-etapa-1): la marca armada, las piezas que
 * salieron y la entrada a Atlas. Al entrar se limpia lo que el onboarding recordaba.
 *
 * Con Cumbre (ADR-2026-10-02-onboarding-v2-listo-con-cumbre): es el cierre, así que festeja.
 * Dice que la marca ya está en Atlas, tira confeti con los colores de la marca y muestra su
 * credencial (logo, nombre, rubro, paleta y las primeras piezas).
 */
export function MarcaLista() {
  const navigate = useNavigate();
  const location = useLocation();
  // "Añadir nueva marca" termina acá también (ADR-2026-10-02-marca-nueva-como-el-onboarding).
  const nueva = enMarcaNueva(location.pathname);
  // "Describí tu marca" desde la home (ADR-2026-10-19-describir-la-marca-con-el-onboarding-nuevo)
  // termina igual que la marca nueva: al estudio, sin catálogo de planes.
  const describir = enDescribirMarca(location.pathname);
  const { plan, isDevTester } = useUserGroup();
  const inicial = useMemo(() => leerEstado(null), []);
  const [marca, setMarca] = useState<MarcaGuardada | null>(null);
  const [esperoMarca, setEsperoMarca] = useState(true);
  const piezas = ((location.state as { piezas?: Array<{ etiqueta: string; url: string }> } | null)?.piezas ?? []).slice(0, 3);
  const [quieto] = useState(pideMenosMovimiento);

  useEffect(() => {
    if (!inicial?.brandId) {
      navigate(nueva ? MARCA_NUEVA.link : describir ? DESCRIBIR_MARCA.link : ONBOARDING_V2.marcaSetup, { replace: true });
      return;
    }
    let vivo = true;
    leerMarca(inicial.brandId)
      .then((m) => vivo && setMarca(m))
      .finally(() => vivo && setEsperoMarca(false));
    // Si la marca tarda, Cumbre habla igual (con "Tu marca").
    const t = window.setTimeout(() => vivo && setEsperoMarca(false), ESPERA_MAX_MARCA_MS);
    return () => {
      vivo = false;
      window.clearTimeout(t);
    };
  }, [inicial?.brandId, navigate, nueva, describir]);

  // La recarga automática de una marca nueva con plan (facturación por marca,
  // ADR-2026-10-29-recarga-automatica-de-marca): la billetera nace con ella prendida y acá se ve y se
  // configura. Sin billetera con plan (una cuenta sin facturación por marca, o el plan que todavía no
  // se acreditó) no hay recuadro.
  const [recarga, setRecarga] = useState<RecargaDeMarca | null>(null);
  useEffect(() => {
    if (!nueva || !inicial?.brandId) return;
    let vivo = true;
    void supabase
      .from('billeteras_de_marca')
      .select('plan, recarga_activa, recarga_creditos, recarga_umbral, recarga_pausada_motivo')
      .eq('brand_id', inicial.brandId)
      .maybeSingle()
      .then(({ data }) => {
        if (vivo && data?.plan) setRecarga(recargaDeBilletera(data));
      });
    return () => {
      vivo = false;
    };
  }, [nueva, inicial?.brandId]);

  // ¿Lo hizo porque empezó a pagar? (ADR-2026-10-31-onboarding-corto-para-la-prueba) Entonces
  // no se le ofrecen planes al final —acaba de comprar uno; el plan cacheado puede ir atrás del
  // webhook—, "Entrar" escribe que ya no lo debe y, si el plan la incluye, sigue a la llamada.
  // `null` mientras se lee: "Entrar" espera esa lectura.
  const [debiaLaMarca, setDebiaLaMarca] = useState<boolean | null>(nueva || describir ? false : null);
  const [saliendo, setSaliendo] = useState(false);
  const [errorAlSalir, setErrorAlSalir] = useState<string | null>(null);
  useEffect(() => {
    if (nueva || describir || !inicial?.companyId) {
      setDebiaLaMarca(false);
      return;
    }
    let vivo = true;
    void supabase
      .from('company_profiles')
      .select('debe_onboarding_marca')
      .eq('id_company', inicial.companyId)
      .maybeSingle()
      .then(
        ({ data }) => vivo && setDebiaLaMarca((data as { debe_onboarding_marca?: boolean } | null)?.debe_onboarding_marca === true),
        () => vivo && setDebiaLaMarca(false),
      );
    return () => {
      vivo = false;
    };
  }, [nueva, describir, inicial?.companyId]);

  const nombre = marca?.nombre || 'Tu marca';

  // ── Cumbre ── (aparece cuando ya sabe el nombre: si no, reescribiría el globo)
  const tramos = useMemo(() => dialogoDelFinal(nombre, piezas.length > 0, nueva), [nombre, piezas.length, nueva]);
  // La cuenta para empezar a hablar arranca cuando Cumbre aparece (con la marca), no al montar.
  const dialogo = useDialogo(tramos, { instantaneo: quieto, arrancarEn: esperoMarca ? 60_000 : 900 });
  const [mostrar, setMostrar] = useState(quieto);
  const [gesto, setGesto] = useState<Gesto | null>(null);
  const [confeti, setConfeti] = useState(false);
  useEffect(() => {
    if (!dialogo.terminado || esperoMarca) return;
    setMostrar(true);
    // El festejo: Cumbre salta y cae confeti, una sola vez.
    setGesto((g) => g ?? { tipo: 'festeja', n: 1 });
    if (!quieto) setConfeti(true);
  }, [dialogo.terminado, esperoMarca, quieto]);

  const colores = marca?.colores.map((c) => c.hex).filter((h) => /^#[0-9a-f]{6}$/i.test(h)) ?? [];
  const paletaDelConfeti = colores.length >= 2 ? colores : COLORES_DE_ATLAS;
  const papelitos = useMemo(
    () =>
      Array.from({ length: PAPELITOS }, (_, i) => ({
        x: (i * 37) % 100,
        demora: (i % 7) * 0.09,
        giro: (i * 53) % 360,
        duracion: 1.6 + ((i * 17) % 10) / 10,
        ancho: 6 + (i % 3) * 3,
      })),
    [],
  );

  /** Sale del onboarding al estudio: con la marca nueva activa, si es una marca nueva. */
  const salir = async () => {
    limpiarEstado();
    // En el teléfono, la cuenta recién creada entra sin avisos sin leer: los únicos que tiene
    // son los "Tu imagen ya está lista" de las primeras piezas, que acaba de ver
    // (ADR-2026-10-23-la-home-del-telefono-para-el-gratis-que-recien-entra). Antes de navegar,
    // así la campana de la home ya cuenta cero. Si falla, se entra igual.
    if (!nueva && !describir && !isPaidPlan(plan) && window.matchMedia('(max-width: 767px)').matches) {
      await marcarLeidas().catch(() => {});
    }
    // Una marca nueva entra al estudio, ya activa (como `RevisarMarca`). La descrita desde la
    // home también: `marcaRecienCompletada` da el paso de la tarjeta por hecho.
    if (nueva || describir) {
      navigate(V2_STUDIO_PATH, { replace: true, state: { marcaRecienCompletada: true } });
      return;
    }
    navigate(V2_STUDIO_PATH, { replace: true });
  };

  /**
   * "Entrar a Atlas". Llevarse una de sus primeras piezas ya no abre antes el catálogo de planes
   * (ADR-2026-10-31-descargar-no-abre-los-planes, supersede
   * ADR-2026-10-05-onboarding-v2-descarga-abre-planes).
   */
  const entrar = async () => {
    if (saliendo) return;
    if (debiaLaMarca && inicial?.companyId) {
      setSaliendo(true);
      setErrorAlSalir(null);
      if (!(await marcarOnboardingDeMarcaHecho(inicial.companyId))) {
        setErrorAlSalir('No pudimos terminar de guardar tu marca. Probá de nuevo.');
        setSaliendo(false);
        return;
      }
      limpiarEstado();
      const destino = destinoAlTerminar(await getUserPlan());
      navigate(destino.ruta, { replace: true, ...(destino.state ? { state: destino.state } : {}) });
      return;
    }
    void salir();
  };

  return (
    <div className="relative isolate flex min-h-[calc(100dvh-var(--om-barra))] flex-col">
      <FondoCordillera quieto={quieto} />

      {confeti && (
        <div className="om-confeti" aria-hidden="true">
          {papelitos.map((p, i) => (
            <span
              key={i}
              style={{
                left: `${p.x}%`,
                width: p.ancho,
                background: paletaDelConfeti[i % paletaDelConfeti.length],
                animationDelay: `${p.demora}s`,
                animationDuration: `${p.duracion}s`,
                ['--giro' as string]: `${p.giro}deg`,
              }}
            />
          ))}
        </div>
      )}

      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-5 px-4 pb-10 pt-6 md:gap-6 md:pt-8">
        {!esperoMarca && (
          <GuiaCumbre
            tramos={tramos}
            letras={dialogo.letras}
            hablando={dialogo.hablando}
            terminado={dialogo.terminado}
            onCompletar={dialogo.completar}
            reaccion={null}
            animo="escucha"
            gesto={gesto}
            instantaneo={quieto}
          />
        )}

        {mostrar && (
          <section className="om-seccion om-credencial" aria-label={`La marca ${nombre}`}>
            <header className="flex items-center gap-4">
              <span className="om-damero om-credencial__logo">
                {marca?.logoUrl ? (
                  <img src={marca.logoUrl} alt={`Logo de ${nombre}`} className="max-h-12 max-w-12 object-contain" />
                ) : (
                  <span className="text-[24px] font-extrabold text-brand-black">{nombre.slice(0, 1)}</span>
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[20px] font-extrabold leading-tight tracking-[-0.02em] text-brand-black">{nombre}</p>
                {marca?.rubro && <span className="om-credencial__rubro">{marca.rubro}</span>}
              </div>
              <span className="om-credencial__sello" aria-hidden="true">
                <span className="material-symbols-rounded">verified</span>
              </span>
            </header>

            {marca && colores.length > 0 && (
              <div className="om-paleta" aria-label="Tu paleta">
                {marca.colores.slice(0, 5).map((c, i) => (
                  <span key={`${c.hex}-${i}`} style={{ background: c.hex }} title={c.name || c.hex} />
                ))}
              </div>
            )}

            {piezas.length > 0 && (
              <div>
                <p className="mb-2.5 text-[13px] font-bold text-[#4B5275]">Tus primeras piezas</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {piezas.map((p, i) => (
                    <img
                      key={p.url}
                      src={p.url}
                      alt={p.etiqueta}
                      className="om-credencial__pieza"
                      style={{ ['--i' as string]: i }}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {mostrar && recarga && inicial?.brandId && (
          <div className="animate-paso-adelante">
            <RecargaAutomatica
              brandId={inicial.brandId}
              recarga={recarga}
              devTester={isDevTester === true}
              onCambio={setRecarga}
            />
          </div>
        )}
      </div>

      {mostrar && (
        <div className="om-barra">
          <div className="om-barra__dentro">
            {errorAlSalir && (
              <p role="alert" className="text-[13px] font-[500] text-[#991B1B]">
                {errorAlSalir}
              </p>
            )}
            <button
              type="button"
              onClick={() => void entrar()}
              disabled={debiaLaMarca === null || saliendo}
              className="om-boton md:w-auto md:px-12"
            >
              {nueva || describir ? `Crear con ${nombre}` : 'Entrar a Atlas'}
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default MarcaLista;
